import { useState, useRef, useEffect, useCallback, useId } from "react";
import type { School } from "../lib/types";

// Uses live API endpoint (proxied in dev, CloudFront /api behavior in prod)
const SCHOOLS_LIST_URL = "/api/schools?limit=20000";

// Cache the schools list to avoid re-fetching
let schoolsCache: School[] | null = null;
let schoolsFetchPromise: Promise<School[]> | null = null;

interface RawSchoolEntry {
  ACARAId: string;
  SchoolName: string;
  City: string;
  StateProvince: string;
  PostalCode: string;
}

async function fetchSchoolsList(): Promise<School[]> {
  if (schoolsCache) return schoolsCache;

  if (schoolsFetchPromise) return schoolsFetchPromise;

  schoolsFetchPromise = (async () => {
    try {
      const response = await fetch(SCHOOLS_LIST_URL);
      if (!response.ok) {
        throw new Error("Failed to fetch schools list");
      }
      const data: RawSchoolEntry[] = await response.json();
      // Map the raw JSON to our School interface
      schoolsCache = data.map((entry) => ({
        acaraId: entry.ACARAId,
        name: entry.SchoolName,
        suburb: entry.City,
        state: entry.StateProvince,
        postcode: entry.PostalCode,
      }));
      return schoolsCache;
    } catch (err) {
      schoolsFetchPromise = null; // Allow retry on error
      throw err;
    }
  })();

  return schoolsFetchPromise;
}

interface SchoolSelectorProps {
  onSelect: (school: School | null, isManual: boolean, manualName?: string) => void;
  selectedSchool: School | null;
  manualEntry?: string;
}

export function SchoolSelector({ onSelect, selectedSchool, manualEntry }: SchoolSelectorProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<School[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showManualInput, setShowManualInput] = useState(!!manualEntry);
  const [manualName, setManualName] = useState(manualEntry || "");
  const [allSchools, setAllSchools] = useState<School[]>([]);
  const [fetchError, setFetchError] = useState(false);
  // Keyboard/ARIA support (WAI-ARIA combobox pattern) — see docs/deep-analysis-2026-07-05.md:
  // the dropdown was previously mouse-only. -1 means no option is highlighted.
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reactId = useId();
  const listboxId = `school-listbox-${reactId}`;
  const optionId = (index: number) => `school-option-${reactId}-${index}`;

  // Fetch schools list on mount
  useEffect(() => {
    fetchSchoolsList()
      .then(setAllSchools)
      .catch(() => setFetchError(true));
  }, []);

  // Search for schools locally when query changes
  const searchSchools = useCallback((searchQuery: string) => {
    if (searchQuery.length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    const lowerQuery = searchQuery.toLowerCase();

    // Filter schools that match the query
    const filtered = allSchools.filter((school) =>
      school.name.toLowerCase().includes(lowerQuery) ||
      school.suburb?.toLowerCase().includes(lowerQuery) ||
      school.postcode?.includes(searchQuery)
    );

    // Sort by relevance: exact start match first, then alphabetically
    filtered.sort((a, b) => {
      const aStartsWith = a.name.toLowerCase().startsWith(lowerQuery);
      const bStartsWith = b.name.toLowerCase().startsWith(lowerQuery);
      if (aStartsWith && !bStartsWith) return -1;
      if (!aStartsWith && bStartsWith) return 1;
      return a.name.localeCompare(b.name);
    });

    setResults(filtered);
    setLoading(false);
  }, [allSchools]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.length >= 2) {
      debounceRef.current = setTimeout(() => {
        searchSchools(query);
      }, 300);
    } else {
      setResults([]);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, searchSchools]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keep the visible option list in one place: 8 school results (existing cap)
  // plus the trailing "enter manually" action, which is also keyboard-reachable.
  const visibleResults = results.slice(0, 8);
  const optionCount = visibleResults.length > 0 ? visibleResults.length + 1 : 0;
  const dropdownVisible = isOpen && (query.length >= 2 || results.length > 0);

  // Reset highlight whenever the option list changes or the dropdown closes,
  // so aria-activedescendant never points at a stale/absent option.
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [results, isOpen]);

  // Keep the highlighted option scrolled into view (dropdown is overflow-y: auto)
  useEffect(() => {
    if (highlightedIndex >= 0) {
      document
        .getElementById(`school-option-${reactId}-${highlightedIndex}`)
        ?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex, reactId]);

  const handleSelectSchool = (school: School) => {
    onSelect(school, false);
    setQuery("");
    setResults([]);
    setIsOpen(false);
    setShowManualInput(false);
  };

  const handleManualEntry = () => {
    setShowManualInput(true);
    setIsOpen(false);
    setResults([]);
    setQuery("");
  };

  const handleManualNameChange = (name: string) => {
    setManualName(name);
    onSelect(null, true, name);
  };

  // Keyboard state machine for the combobox input:
  //   closed --ArrowDown/ArrowUp--> open (no highlight)
  //   open   --ArrowDown/ArrowUp--> move highlight (wraps at both ends)
  //   open+highlight --Enter--> select option (school, or manual entry if last)
  //   open   --Escape--> closed (focus stays on input)
  //   open   --Tab--> closed (focus moves on natively)
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!dropdownVisible) {
          setIsOpen(true);
        } else if (optionCount > 0) {
          setHighlightedIndex((prev) => (prev + 1) % optionCount);
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (!dropdownVisible) {
          setIsOpen(true);
        } else if (optionCount > 0) {
          setHighlightedIndex((prev) => (prev <= 0 ? optionCount - 1 : prev - 1));
        }
        break;
      case "Enter":
        // Only intercept Enter when an option is highlighted; otherwise keep
        // the default (e.g. form submit) behavior unchanged.
        if (dropdownVisible && highlightedIndex >= 0 && highlightedIndex < optionCount) {
          e.preventDefault();
          if (highlightedIndex < visibleResults.length) {
            handleSelectSchool(visibleResults[highlightedIndex]);
          } else {
            handleManualEntry();
          }
        }
        break;
      case "Escape":
        if (dropdownVisible) {
          e.preventDefault();
          setIsOpen(false);
        }
        break;
      case "Tab":
        // Close on Tab but let focus move on naturally.
        setIsOpen(false);
        break;
    }
  };

  const handleClearSelection = () => {
    onSelect(null, false);
    setShowManualInput(false);
    setManualName("");
    setQuery("");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  // If a school is selected, show selected state
  if (selectedSchool && !showManualInput) {
    return (
      <div className="school-selector">
        <div className="school-selected">
          <div className="school-selected-info">
            <span className="school-selected-name">{selectedSchool.name}</span>
            {selectedSchool.suburb && selectedSchool.state && (
              <span className="school-selected-location">
                {selectedSchool.suburb}, {selectedSchool.state} {selectedSchool.postcode}
              </span>
            )}
          </div>
          <button type="button" className="school-clear-btn" onClick={handleClearSelection}>
            Change
          </button>
        </div>
      </div>
    );
  }

  // Manual entry mode
  if (showManualInput) {
    return (
      <div className="school-selector">
        <div className="form-group">
          <label htmlFor="manualSchoolName">School name *</label>
          <input
            id="manualSchoolName"
            type="text"
            value={manualName}
            onChange={(e) => handleManualNameChange(e.target.value)}
            placeholder="Enter your school name"
            required
          />
          <p className="form-hint">
            Your order will be verified by our team.{" "}
            <button type="button" className="link-btn" onClick={handleClearSelection}>
              Search for school instead
            </button>
          </p>
        </div>
      </div>
    );
  }

  // Search mode
  return (
    <div className="school-selector" ref={containerRef}>
      <div className="form-group">
        <label htmlFor="schoolSearch">Search for your school *</label>
        {fetchError ? (
          <p className="form-hint">
            Unable to load schools list.{" "}
            <button type="button" className="link-btn" onClick={handleManualEntry}>
              Enter school name manually
            </button>
          </p>
        ) : (
          <>
            <div className="school-search-wrapper">
              <input
                ref={inputRef}
                id="schoolSearch"
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setIsOpen(true);
                }}
                onFocus={() => setIsOpen(true)}
                onKeyDown={handleSearchKeyDown}
                placeholder="Start typing your school name..."
                autoComplete="off"
                disabled={allSchools.length === 0}
                role="combobox"
                aria-expanded={dropdownVisible}
                aria-autocomplete="list"
                aria-controls={listboxId}
                aria-activedescendant={
                  dropdownVisible && highlightedIndex >= 0 ? optionId(highlightedIndex) : undefined
                }
              />
              {allSchools.length === 0 && !fetchError && (
                <span className="school-search-loading">Loading schools...</span>
              )}
              {loading && allSchools.length > 0 && (
                <span className="school-search-loading">Searching...</span>
              )}
            </div>

            {dropdownVisible && (
              <div
                className="school-dropdown"
                id={listboxId}
                role="listbox"
                aria-label="School search results"
              >
                {results.length > 0 ? (
                  <>
                    {visibleResults.map((school, index) => (
                      <button
                        key={school.acaraId}
                        type="button"
                        id={optionId(index)}
                        role="option"
                        aria-selected={highlightedIndex === index}
                        className={`school-dropdown-item${highlightedIndex === index ? " highlighted" : ""}`}
                        onClick={() => handleSelectSchool(school)}
                        onMouseEnter={() => setHighlightedIndex(index)}
                        tabIndex={-1}
                      >
                        <span className="school-dropdown-name">{school.name}</span>
                        {school.suburb && school.state && (
                          <span className="school-dropdown-location">
                            {school.suburb}, {school.state} {school.postcode}
                          </span>
                        )}
                      </button>
                    ))}
                    <button
                      type="button"
                      id={optionId(visibleResults.length)}
                      role="option"
                      aria-selected={highlightedIndex === visibleResults.length}
                      className={`school-dropdown-manual${highlightedIndex === visibleResults.length ? " highlighted" : ""}`}
                      onClick={handleManualEntry}
                      onMouseEnter={() => setHighlightedIndex(visibleResults.length)}
                      tabIndex={-1}
                    >
                      Can't find your school? Enter manually
                    </button>
                  </>
                ) : query.length >= 2 && !loading ? (
                  <div className="school-dropdown-empty">
                    <p>No schools found matching "{query}"</p>
                    <button type="button" className="link-btn" onClick={handleManualEntry}>
                      Enter school name manually
                    </button>
                  </div>
                ) : null}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
